import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary'; // eslint-disable-line no-unused-vars
import '../styles/RouteErrorBoundary.css';

/**
 * Route-specific Error Boundary that provides navigation context
 * and route-specific error handling for page-level components
 *
 * Note: This must be a class component since React error boundaries
 * can only be implemented with class components
 */
class RouteErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error: error
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('RouteErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Report route-specific error with context
    this.reportRouteError(error, errorInfo);
  }

  reportRouteError = (error, errorInfo) => {
    const routeContext = {
      pathname: this.props.location?.pathname,
      search: this.props.location?.search,
      state: this.props.location?.state,
      routeName: this.props.routeName
    };

    console.error('Route error context:', routeContext);

    // In production, send to error tracking with route context
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, {
      //   contexts: {
      //     react: errorInfo,
      //     route: routeContext
      //   }
      // });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  }

  render() {
    if (this.state.hasError) {
      const routeFallback = (error, errorInfo, onRetry) => (
        <div className="route-error-boundary">
          <div className="route-error-content">
            <div className="route-error-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h3 className="route-error-title">
              Page Error
            </h3>

            <p className="route-error-message">
              This page encountered an error and couldn't load properly.
              {this.props.routeName && ` (Route: ${this.props.routeName})`}
            </p>

            <div className="route-error-actions">
              <button
                className="btn btn-primary"
                onClick={onRetry}
                aria-label="Retry loading page"
              >
                Retry
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={() => this.props.navigate('/')}
                aria-label="Go to dashboard"
              >
                Go to Dashboard
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={() => this.props.navigate(-1)}
                aria-label="Go back to previous page"
              >
                Go Back
              </button>
            </div>

            {/* Development info */}
            {process.env.NODE_ENV === 'development' && (
              <details className="route-error-details">
                <summary>Route Error Details (Development)</summary>
                <div className="route-error-debug">
                  <h4>Current Route:</h4>
                  <pre>{JSON.stringify(this.props.location, null, 2)}</pre>

                  <h4>Error:</h4>
                  <pre>{error.message}</pre>

                  <h4>Stack:</h4>
                  <pre>{error.stack}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );

      // Use the custom fallback UI directly
      return routeFallback(this.state.error, this.state.errorInfo, this.handleRetry);
    }

    return this.props.children;
  }
}

/**
 * Functional wrapper to provide React Router hooks to the class component
 */
const RouteErrorBoundary = ({ children, routeName }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <RouteErrorBoundaryClass
      navigate={navigate}
      location={location}
      routeName={routeName}
    >
      {children}
    </RouteErrorBoundaryClass>
  );
};

RouteErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  routeName: PropTypes.string
};

export default RouteErrorBoundary;